import { useIntl } from 'react-intl';

import { IOfferDiscount, ISanityPicker, ISanityVendorConfigs } from '@rbi-ctg/menu';
import { MenuObjectTypes, OfferDiscountTypes } from 'enums/menu';
import { getPosVendorFromStore } from 'hooks/menu/use-pos-vendor';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { dollarsToCents } from 'utils/index';
import { IWithVendorConfig, PluTypes, PosVendors } from 'utils/vendor-config';

export const useOfferPrice = () => {
  const isAmountOfferDiscountValueAsCents = useFlag(
    LaunchDarklyFlag.ENABLE_AMOUNT_OFFER_DISCOUNT_VALUE_AS_CENTS
  );

  const enableShowOfferPrices = useFlag(LaunchDarklyFlag.ENABLE_PRICES_IN_OFFERS_PAGE);

  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();

  const { prices, store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const vendor = getPosVendorFromStore(store, serviceMode);
  const posVendor: PosVendors = vendor!;

  const shouldShowOfferCardPrice = !!prices && !!vendor && enableShowOfferPrices;

  const getPriceFromVendorConfig = (vendorConfig: ISanityVendorConfigs | null | undefined) => {
    if (!prices) {
      return null;
    }

    if (!vendor) {
      return null;
    }

    if (!vendorConfig || !vendorConfig[posVendor]) {
      return null;
    }

    if (vendorConfig[posVendor].pluType === PluTypes.CONSTANT) {
      const plu = vendorConfig[posVendor].constantPlu;
      return prices[plu] ?? null;
    }

    return null;
  };

  const getPickerOfferPrice = (offer: LoyaltyOffer) => {
    const picker = offer.incentives?.[0] as unknown as ISanityPicker;
    const defaultOption = picker.options.find(option => !!option.default) || picker.options[0];

    const incentive = defaultOption.option as IWithVendorConfig;
    const vendorConfig = incentive?.vendorConfigs;

    return getPriceFromVendorConfig(vendorConfig);
  };

  const getItemComboOfferPrice = (offer: LoyaltyOffer) => {
    const item = offer.incentives?.[0] as IWithVendorConfig;
    const vendorConfig = item.vendorConfigs;

    return getPriceFromVendorConfig(vendorConfig);
  };

  const getOfferIncentivePrice = (offer: LoyaltyOffer) => {
    const incentive = offer.incentives?.[0];

    if (!incentive) {
      return null;
    }

    if (incentive._type === MenuObjectTypes.COMBO || incentive._type === MenuObjectTypes.ITEM) {
      return getItemComboOfferPrice(offer);
    } else if (incentive._type === MenuObjectTypes.PICKER) {
      return getPickerOfferPrice(offer);
    }

    return null;
  };

  const getOfferPrice = (offer: LoyaltyOffer) => {
    const { vendorConfigs } = offer as { vendorConfigs: ISanityVendorConfigs };
    return getPriceFromVendorConfig(vendorConfigs);
  };

  // Scenarios for calculation - https://rbictg.atlassian.net/wiki/spaces/EGMT/pages/5667815639/Solution+Loyalty+2.0+Experiments#3.2.-Scenario-diagrams
  const getOfferCardPrice = (offer: LoyaltyOffer) => {
    let originalPrice: number | null = null;

    const result: {
      slashedPrice: string | null;
      priceToShow: string | null;
    } = {
      slashedPrice: null,
      priceToShow: null,
    };

    if (!isDiscountOffer(offer)) {
      const offerPrice = getOfferPrice(offer);
      const incentivePrice = getOfferIncentivePrice(offer);

      if (offerPrice === null && incentivePrice === null) {
        result.priceToShow = null;
        result.slashedPrice = null;
        return result;
      }

      if (offerPrice === null || incentivePrice === null) {
        result.slashedPrice = null;

        const discountedPrice =
          offerPrice !== null ? offerPrice : incentivePrice !== null ? incentivePrice : null;

        if (discountedPrice === null) {
          result.priceToShow = null;
          result.slashedPrice = null;
          return result;
        }

        if (discountedPrice === 0) {
          result.priceToShow = formatMessage({ id: 'free' });
          return result;
        }

        result.priceToShow = formatCurrencyForLocale(discountedPrice);
        return result;
      }

      result.priceToShow =
        offerPrice === 0 ? formatMessage({ id: 'free' }) : formatCurrencyForLocale(offerPrice);

      if (offerPrice === incentivePrice) {
        result.slashedPrice = null;
      } else {
        result.slashedPrice = formatCurrencyForLocale(incentivePrice);
      }

      return result;
    }

    const discount = offer?.incentives?.[0] as IOfferDiscount;
    const discountProduct = discount.discountProduct;
    const { discountValue, discountType } = discount ?? {};

    if (!discountProduct) {
      result.slashedPrice = null;

      if (!discountValue) {
        result.priceToShow = null;
        return result;
      }

      if (discountType === OfferDiscountTypes.AMOUNT) {
        const discountedValue = Math.abs(
          isAmountOfferDiscountValueAsCents ? discountValue : dollarsToCents(discountValue)
        );
        result.priceToShow = `${formatCurrencyForLocale(discountedValue)} ${formatMessage({ id: 'off' })}`;
        return result;
      } else {
        if (discountValue >= 100) {
          result.priceToShow = formatMessage({ id: 'free' });
          return result;
        }

        result.priceToShow = `${discountValue}% ${formatMessage({ id: 'off' })}`;
        return result;
      }
    }

    const vendorConfig = discountProduct.vendorConfigs;

    if (vendorConfig && vendorConfig[posVendor]?.pluType === PluTypes.CONSTANT) {
      const plu = vendorConfig[posVendor].constantPlu;
      originalPrice = prices?.[plu] ?? null;
    }

    if (!originalPrice) {
      result.slashedPrice = null;

      if (!discountValue) {
        result.priceToShow = null;
        return result;
      }

      if (discountType === OfferDiscountTypes.AMOUNT) {
        const discountValueInCents = Math.abs(
          isAmountOfferDiscountValueAsCents ? discountValue : dollarsToCents(discountValue)
        );
        result.priceToShow = `${formatCurrencyForLocale(discountValueInCents)} ${formatMessage({ id: 'off' })}`;
      } else if (discountType === OfferDiscountTypes.PERCENTAGE) {
        if (discountValue >= 100) {
          result.priceToShow = formatMessage({ id: 'free' });
          return result;
        }

        result.priceToShow = `${discountValue}% ${formatMessage({ id: 'off' })}`;
      }
      return result;
    }

    if (!discountValue) {
      result.slashedPrice = null;
      result.priceToShow = formatCurrencyForLocale(originalPrice);
      return result;
    }

    result.slashedPrice = formatCurrencyForLocale(originalPrice);
    if (discountType === OfferDiscountTypes.AMOUNT) {
      const discountValueInCents = Math.abs(
        isAmountOfferDiscountValueAsCents ? discountValue : dollarsToCents(discountValue)
      );
      const discountedValue =
        discountValueInCents >= originalPrice ? 0 : originalPrice - discountValueInCents;
      result.priceToShow = discountedValue
        ? formatCurrencyForLocale(discountedValue)
        : formatMessage({ id: 'free' });
    } else if (discountType === OfferDiscountTypes.PERCENTAGE) {
      if (discountValue >= 100) {
        result.priceToShow = formatMessage({ id: 'free' });
        return result;
      }

      const discountPercentage = discountValue / 100;
      const discountValueInCents = Math.round(originalPrice * discountPercentage);
      result.priceToShow = formatCurrencyForLocale(originalPrice - discountValueInCents);
    }
    return result;
  };

  return {
    getOfferCardPrice,
    shouldShowOfferCardPrice,
  };
};
